import {
  IconKeyboardArrowRightMS,
  IconKeyboardArrowUp,
} from '@/constants/icons';
import { useState } from 'react';

import { TimelineItemProps } from './data';
import {
  AuditTag,
  GroupCount,
  GroupHeader,
  GroupItems,
  GroupLabel,
  GroupSummary,
  ItemBadge,
  ItemContainer,
  ItemIndicator,
  ItemLine,
  ItemTime,
} from './styles';
import { TimelineItem } from './TimelineItem';

type TimelineGroupProps = {
  label: string;
  items: TimelineItemProps[];
  isFirst: boolean;
  isLast: boolean;
  onActivityClick?: (delegatedSessionId: number) => void;
};

export function TimelineGroup({
  label,
  items,
  isFirst,
  isLast,
  onActivityClick,
}: TimelineGroupProps): JSX.Element {
  const [open, setOpen] = useState(false);

  // Events may arrive newest-first, so read the range off the sorted times
  // instead of assuming the group's own order.
  const times = [...items.map((item) => item.time)].sort();
  const earliest = times[0];
  const latest = times[times.length - 1];
  const timeRange = earliest === latest ? earliest : `${earliest} – ${latest}`;

  const status = items.some((item) => item.status === 'error')
    ? 'error'
    : 'success';
  const hasAuditable = items.some((item) => item.isAuditable);

  return (
    <>
      <ItemContainer>
        <ItemTime>{timeRange}</ItemTime>

        <ItemIndicator>
          <ItemLine isFirst={isFirst} isLast={isLast && !open} />
          <ItemBadge status={status} />
        </ItemIndicator>

        <GroupHeader type="button" onClick={() => setOpen((prev) => !prev)}>
          <GroupSummary>
            <GroupLabel title={label}>{label}</GroupLabel>
            <GroupCount>{items.length}</GroupCount>
            {hasAuditable && <AuditTag>crítico</AuditTag>}
          </GroupSummary>
          {open ? IconKeyboardArrowUp : IconKeyboardArrowRightMS}
        </GroupHeader>
      </ItemContainer>

      {open && (
        <GroupItems>
          {items.map((item, index) => {
            const { delegatedSessionId } = item;

            return (
              <TimelineItem
                key={item.id}
                id={item.id}
                time={item.time}
                message={item.message}
                status={item.status}
                isAuditable={item.isAuditable}
                isLink={item.isLink}
                isFirst={index === 0}
                isLast={index === items.length - 1}
                errorType={item.errorType}
                errorMessage={item.errorMessage}
                onClick={
                  delegatedSessionId !== undefined && onActivityClick
                    ? () => onActivityClick(delegatedSessionId)
                    : undefined
                }
              />
            );
          })}
        </GroupItems>
      )}
    </>
  );
}
